import { apiSlice } from '../api/apiSlice'
import dayjs from 'dayjs'
import { transformResponseToWorkActivity } from '../../utils/transformResponseToWorkActivity'

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getSchedule: builder.query<WorkActivity, ArgTypes>({
      query: ({ employee, date }) => `/employees/${employee}/work-activity?date=${date}`,
      transformResponse: (responseData: { plannedHours: PlannedHours; trackedHours: PlannedHours }) => {
        return transformResponseToWorkActivity(responseData)
      },
      providesTags: () => [{ type: 'WorkActivity', id: 'LIST' }],
    }),
    editSchedule: builder.mutation<
      WorkActivity,
      {
        employee: string
        status: string
        requestBody: EditScheduleRequest
        date: string
      }
    >({
      query: ({ employee, status, requestBody, date }) => ({
        url: `/employees/${employee}/work-activity?status=${status}&date=${date}`,
        method: 'POST',
        body: requestBody,
      }),
      transformResponse: (responseData: { plannedHours: PlannedHours; trackedHours: PlannedHours }) => {
        return transformResponseToWorkActivity(responseData)
      },
      onQueryStarted: async ({ employee, status, requestBody, date }, { dispatch, queryFulfilled }) => {
        const { data: responseData } = await queryFulfilled
        const patchResult = dispatch(
          extendedApiSlice.util.updateQueryData('getSchedule', { employee, date }, draft => {
            Object.assign(draft, responseData)
          })
        )
        queryFulfilled.catch(patchResult.undo)
      },
      invalidatesTags: () => [{ type: 'WorkActivityHistory', id: 'LIST' }],
    }),
    copyWorkActivities: builder.mutation<WorkActivity, { employeeId: string; dates: string[]; status: string }>({
      query: ({ employeeId, dates, status }) => ({
        url: `/employees/${employeeId}/work-activity/copy?status=${status}`,
        method: 'POST',
        body: dates,
      }),
      transformResponse: (responseData: { plannedHours: PlannedHours; trackedHours: PlannedHours }) => {
        return transformResponseToWorkActivity(responseData)
      },
      onQueryStarted: async ({ employeeId, dates, status }, { dispatch, queryFulfilled }) => {
        const { data: responseData } = await queryFulfilled
        const patchResult = dispatch(
          extendedApiSlice.util.updateQueryData(
            'getSchedule',
            {
              employee: employeeId,
              date: dates.length > 0 ? dayjs(dates[0]).format('YYYY-MM') : dayjs().format('YYYY-MM'),
            },
            draft => {
              Object.assign(draft, responseData)
            }
          )
        )
        queryFulfilled.catch(patchResult.undo)
      },
      invalidatesTags: () => [{ type: 'WorkActivityHistory', id: 'LIST' }],
    }),
  }),
})

type ArgTypes = {
  employee: string | undefined
  date: string
}

type TimeSlot = {
  id: string
  from: string
  to: string
  notShow?: boolean
}

type SendingTimeSlot = {
  from: string
  to: string
}

export type PlannedHours = {
  [date: string]: TimeSlot[]
}

export type TransformedPlannedHours = {
  date: string
  timeSlots: TimeSlot[]
}

export type WorkActivity = {
  plannedHours: TransformedPlannedHours[]
  trackedHours: TransformedPlannedHours[]
}

export type FrequencyValue = {
  frequency: number
  selectedDays: string[]
}

export type EditScheduleRequest = {
  startDate: string
  endDate: string
  hours: SendingTimeSlot[]
  frequency: string
  frequencyValue: FrequencyValue
}

export const { useGetScheduleQuery, useEditScheduleMutation, useCopyWorkActivitiesMutation } = extendedApiSlice